import {Notebook, Wrench} from '@phosphor-icons/react';
import {bool, func, object} from 'prop-types';
import {Fragment, useState} from 'react';

import {useAddDayExercise, useMuscleGroups, useReplaceDayExercise} from '../api.js';
import {MAX_EXERCISES_PER_DAY} from '../constants.js';
import {useNotifierContext} from '../NotifierContext.jsx';
import {
  useActiveExercises,
  useExercisesForMuscleGroup,
  useMutationCallback,
} from '../utils/hooks.js';
import {classNames} from '../utils/index.js';
import {runAfterAnimations} from '../utils/misc.js';
import ExerciseForm from './ExerciseForm.jsx';
import ExerciseRepeatSwitch from './ExerciseRepeatSwitch.jsx';
import Dialog from './ui/Dialog.jsx';
import Message from './ui/Message.jsx';
import Select from './ui/Select.jsx';

const tabActive = 'text-base-content border-accent';
const tabInActive = 'text-base-content/50 border-gray-300/50 hover:border-gray-400/50';
const tabDefault =
  'group flex justify-center items-center gap-3 py-4 px-4 font-medium text-sm cursor-pointer w-full py-3 px-1 text-center border-b-2 font-bold text-sm transition';

/**
  This component allows users to add dayExercises or update a dayExercise.exerciseId from within their mesocycles.

  When adding a dayExercise, they must first specify a muscleGroup and then an exerciseId from the list generated from the muscleGroup.

  When updating a dayExercise.exerciseId, they are required to update it with an exerciseId from the same muscleGroup.
    If they want to replace the exerciseId with one from a different muscle group, they will need to delete
    the dayExercise altogether and add a brand new one.

    Currently we don't allow a dayExercise to be deleted if there are performed sets. We should force the user to explicitly delete
    their own performed sets in order for us to then allow them to delete the dayExercise
 */

// TODO: Rename this to some thing like DayExerciseEditor
export default function ExerciseSelector({day, isOpen, onClose, dayExercise}) {
  const {data: groups} = useMuscleGroups({enabled: isOpen});
  const {showNotification} = useNotifierContext();
  const exercises = useActiveExercises();

  const [groupId, setGroupId] = useState(dayExercise?.muscleGroupId);
  const [selectedExerciseId, setSelectedExerciseId] = useState(dayExercise?.exerciseId);
  const [tab, setTab] = useState(0);
  const [repeat, setRepeat] = useState(false);
  const [ignoreWarning, setIgnoreWarning] = useState(false);

  const exercisesForMuscleGroup = useExercisesForMuscleGroup(groupId);
  const addDayExercise = useAddDayExercise(day.id, {meta: {action: 'addDayExercise'}});
  const replaceDayExercise = useReplaceDayExercise(day.id, dayExercise?.id);

  const handleClose = useMutationCallback(() => {
    onClose();

    runAfterAnimations(() => {
      setIgnoreWarning(false);
      setRepeat(false);
    });
  }, [onClose]);

  const handleReplaceDayExercise = useMutationCallback(
    (newExerciseId, exercises) => {
      const body = {
        newExerciseId: newExerciseId || selectedExerciseId,
        repeat,
      };

      replaceDayExercise.mutate(body, {
        onSuccess: () => {
          const oldName = exercises.find(ex => ex.id === dayExercise.exerciseId)?.name;
          const newName = exercises.find(ex => ex.id === newExerciseId)?.name;
          handleClose();
          setSelectedExerciseId(newExerciseId);
          showNotification({
            message: `Exercise ${oldName} replaced with ${newName}!`,
            type: 'success',
            autoClose: true,
          });
        },
      });
    },
    [dayExercise, handleClose, repeat, replaceDayExercise, selectedExerciseId, showNotification]
  );

  const handleAddDayExercise = useMutationCallback(
    (exerciseId, exercises) => {
      // We omit a position here, so the exercise will be added at the end of the workout by default
      const body = {
        exerciseId,
        repeat,
      };

      addDayExercise.mutate(body, {
        onSuccess: () => {
          const newExercise = exercises.find(ex => ex.id === exerciseId);

          handleClose();
          setGroupId(newExercise?.muscleGroupId);
          showNotification({
            message: `Exercise ${newExercise?.name} added!`,
            type: 'success',
            autoClose: true,
          });
        },
      });
    },
    [repeat, addDayExercise, handleClose, showNotification]
  );

  const tooManyExercisesAlready = day.exercises.length > MAX_EXERCISES_PER_DAY;
  const addingOneTooMany = day.exercises.length + 1 > MAX_EXERCISES_PER_DAY;

  const isReplacing = Boolean(dayExercise);
  const warning = dayExercise?.sets.some(ex => !!ex.finishedAt);
  const isWorking = addDayExercise.isWorking || replaceDayExercise.isWorking;

  const title =
    warning && !ignoreWarning
      ? 'Are you sure?'
      : (!warning || ignoreWarning) && isReplacing
      ? 'Replace exercise'
      : 'Add new exercise';

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title={title}>
      {warning && !ignoreWarning && (
        <Fragment>
          <Message variant="warning" className="shadow-sm">
            Replacing this exercise will reset all of its sets
          </Message>
          <p className="text-sm">
            This exercise has sets that have already been performed. When you replace it with
            another exercise, all existing sets will be marked as incomplete and their weight and
            reps will be reset.
          </p>
          <div className="flex items-center justify-end gap-4">
            <button className="btn btn-ghost" onClick={handleClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={() => setIgnoreWarning(true)}>
              Ignore
            </button>
          </div>
        </Fragment>
      )}

      {(!warning || ignoreWarning) && (
        <Fragment>
          <div className="mb-4 flex w-full">
            <div className="grow">
              <nav className="-mb-px flex" aria-label="Tabs">
                <span
                  onClick={() => setTab(0)}
                  className={classNames(tab === 0 ? tabActive : tabInActive, tabDefault)}
                >
                  <Notebook className="h-5 w-5" />
                  <span>From library</span>
                </span>
                <span
                  onClick={() => setTab(1)}
                  className={classNames(tab === 1 ? tabActive : tabInActive, tabDefault)}
                >
                  <Wrench className="h-5 w-5" />
                  <span>Custom</span>
                </span>
              </nav>
            </div>
          </div>

          {!isReplacing && (tooManyExercisesAlready || addingOneTooMany) && (
            <Message variant="warning" className="my-4 shadow-sm">
              {tooManyExercisesAlready
                ? `This day already exceeds the recommended max of ${MAX_EXERCISES_PER_DAY}`
                : `Adding an exercise to this day exceeds the recommended max of ${MAX_EXERCISES_PER_DAY}`}
            </Message>
          )}

          {tab === 0 && (
            <Fragment>
              <div className="space-y-3">
                <Select
                  onChange={e => {
                    setSelectedExerciseId('');
                    setGroupId(parseInt(e.target.value));
                  }}
                  value={groupId || ''}
                  label="Muscle group"
                  disabled={isReplacing}
                >
                  <option value="" disabled>
                    Choose muscle group
                  </option>
                  {groups &&
                    groups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                </Select>

                <Select
                  onChange={e => setSelectedExerciseId(parseInt(e.target.value))}
                  value={selectedExerciseId || ''}
                  label="Exercise"
                  disabled={!groupId}
                >
                  <option value="" disabled>
                    Choose an exercise
                  </option>
                  {exercisesForMuscleGroup?.map(ex => (
                    <option
                      key={ex.id}
                      value={ex.id}
                      disabled={
                        isReplacing && day.exercises.find(dayEx => dayEx.exerciseId === ex.id)
                      }
                    >
                      {ex.name}
                    </option>
                  ))}
                </Select>

                {/* TODO: EITHER USE CHECKBOX HERE OR USE SWITCH ON UPDATE DAY LABEL DIALOG  */}
                <div className="!mt-6">
                  <ExerciseRepeatSwitch repeat={repeat} setRepeat={setRepeat} />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button className="btn btn-ghost mr-4" onClick={handleClose}>
                  Cancel
                </button>

                <button
                  className="btn btn-accent"
                  disabled={
                    dayExercise?.exerciseId === selectedExerciseId ||
                    !selectedExerciseId ||
                    isWorking
                  }
                  onClick={() =>
                    isReplacing
                      ? handleReplaceDayExercise(selectedExerciseId, exercises.all)
                      : handleAddDayExercise(selectedExerciseId, exercises.all)
                  }
                >
                  {isWorking && <span className="loading"></span>}
                  {isReplacing ? 'Replace' : 'Add'}
                </button>
              </div>
            </Fragment>
          )}

          {tab === 1 && (
            <ExerciseForm
              onClose={handleClose}
              muscleGroupId={isReplacing ? Number(groupId) : null}
              onSuccess={isReplacing ? handleReplaceDayExercise : handleAddDayExercise}
              saveLabel={`Create and ${isReplacing ? 'Replace' : 'Add'}`}
            >
              <ExerciseRepeatSwitch repeat={repeat} setRepeat={setRepeat} />
            </ExerciseForm>
          )}
        </Fragment>
      )}
    </Dialog>
  );
}

ExerciseSelector.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  dayExercise: object,
  day: object.isRequired,
};
